import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import Button from '@/src/components/button'
import { useForm } from 'react-hook-form'
import { FormTextField } from '@/src/components/form-components'
import CommonApi from '@/pages/api'
import { useStore } from '@/src/types/store'
import { yupResolver } from '@hookform/resolvers/yup'
import { SignUpSchema } from '@/src/types'
import CustomModal from '@/src/components/Modal'
import AwsCaptchaComponent from '@/src/components/captcha/Captcha'
import { useTier } from '@/src/hooks/useTier'

interface FormData {
  first_name: string
  last_name: string
  email: string
}

const InformedSection = ({ sectionId, isDraft }: any) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control
  } = useForm<FormData>({
    resolver: yupResolver(SignUpSchema)
  })
  const { homeTheme, TextColorType, openToast } = useStore()
  // /register-contact is closed on Intelligence tenancies, so the form would
  // only be able to fail after submit. Hide it instead of erroring.
  const { canWidgetSignup } = useTier()
  const [loading, setLoading] = useState<boolean>(false)
  const [showCaptcha, setShowCaptcha] = useState(false)
  const [pendingValues, setPendingValues] = useState<FormData | null>(null)

  const handlePassCaptcha = (data: FormData) => {
    setPendingValues(data)
    setShowCaptcha(true)
  }

  const onCaptchaSuccess = () => {
    setShowCaptcha(false)
    if (pendingValues) {
      const valuesToSubmit = pendingValues
      setPendingValues(null)
      formSubmit(valuesToSubmit)
    }
  }

  const formSubmit = async (data: FormData) => {
    setLoading(true)
    const payload = {
      first_name: data?.first_name,
      last_name: data?.last_name,
      email: data?.email,
      code: homeTheme?.code
    }
    try {
      const response = await CommonApi({
        path: `register-contact`,
        method: 'post',
        data: payload
      })

      if (response.status) {
        openToast('Thank you for signing up.', 'success')
        reset()
        setPendingValues(null)
      }
    } catch (error: any) {
      console.error('Error fetching business users:', error)
      openToast(error?.response?.data?.msg || 'Something went wrong', 'error')
    } finally {
      setLoading(false)
    }
  }

  if (!canWidgetSignup) return null

  return (
    <Box className="flex justify-center py-[54px] bg-[#07004D14] gap-2.5 mb-8">
      <Box className="flex justify-center p-2 lg:p-0">
        <form onSubmit={handleSubmit(handlePassCaptcha)}>
          <Typography variant="h5">Stay Informed</Typography>
          <Typography variant="body1" sx={{ marginTop: '12px' }}>
            Be the first to receive ASX announcements, investor insights and
            <br /> company updates.
          </Typography>
          <Box className="flex flex-col sm:flex-row gap-4 mt-4">
            <FormTextField
              validation={register('first_name')}
              label="First Name"
              divClass="col-span-full"
              error={!!errors.first_name}
              helperText={errors.first_name?.message}
            />
            <FormTextField
              validation={register('last_name')}
              label="Last Name"
              divClass="col-span-full"
              error={!!errors.last_name}
              helperText={errors.last_name?.message}
            />
          </Box>
          <Box className="mt-4">
            <FormTextField
              validation={register('email')}
              label="Email"
              divClass="col-span-full"
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          </Box>
          <Box className=" mt-4">
            <Button
              classes="w-full text-center rounded-[100px]"
              variant="contained"
              text="Submit"
              extraClass={` bg-primary-home ${TextColorType?.primaryTextDark ? 'text-white' : 'text-black'}  `}
              customStyle={true}
              type="submit"
              loading={loading}
            />
          </Box>
        </form>
      </Box>

      <CustomModal
        crossIcon={true}
        classes=" w-[37.5rem] h-fit min-h-[34.375rem] p-4"
        open={showCaptcha}
        handleClose={() => {
          setShowCaptcha(false)
          setPendingValues(null)
        }}
      >
        {showCaptcha && <AwsCaptchaComponent onCaptchaSuccess={onCaptchaSuccess} />}
      </CustomModal>
    </Box>
  )
}

export default InformedSection
